import { useCallback, useEffect, useRef } from 'react';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import { loadSpatialDecoder, type SpatialDecoderModule } from '../../spatial/decode/loadSpatialDecoder';
import type { SpatialColumnDeclaration } from '../utils/spatialValueAccessor';
import type { CopyValueTransform } from './useCellSelection';

const EMPTY_DECLARED: ReadonlyMap<string, SpatialColumnDeclaration> = new Map();

type SpatialCopyContext = {
  ids: ReadonlySet<string>;
  engine: DatabaseEngineId | null;
  dataTypeOf: (columnId: string) => string | undefined;
  declared: ReadonlyMap<string, SpatialColumnDeclaration>;
  decoder: SpatialDecoderModule | null;
};

/**
 * Builds the copy/export `CopyValueTransform` for native-spatial columns
 * (T5 — WKT copy/export parity). Shared by ResultsGrid (Query Editor) and
 * TableExplorerGrid so both grids' Ctrl+C / context-menu / header "Copy
 * values" seams emit WKT for the same set of columns the same way.
 *
 * Preloads the lazy spatial decode chunk (memoized — one chunk load per
 * session, via `loadSpatialDecoder`) as soon as `nativeSpatialColumnIds` is
 * non-empty, so the transform is very likely already resolved by the time a
 * user triggers a copy action. Bundle isolation: this hook's own import graph
 * (`loadSpatialDecoder.ts`) never statically pulls wkx/the decoder core — only
 * the dynamic `import()` inside `loadSpatialDecoder` does, once awaited here.
 *
 * If the chunk hasn't resolved yet (rare race, e.g. a copy fired within the
 * first render after spatial columns appear), the transform is a no-op
 * passthrough for that call — never blocks, never throws.
 */
export function useSpatialCopyTransform(
  nativeSpatialColumnIds: ReadonlySet<string>,
  engine: DatabaseEngineId | null,
  dataTypeOf: (columnId: string) => string | undefined,
  /** T18 — declared coordinate columns (columnId → declaration). Single-column
   *  declared formats (WKT/GeoJSON/latlng-text) copy as WKT; a lat-lng pair
   *  stays RAW (A8/CD5), so its lat column is a no-op passthrough here. */
  declaredSpatialColumns: ReadonlyMap<string, SpatialColumnDeclaration> = EMPTY_DECLARED,
): CopyValueTransform {
  // "Latest value" ref pattern (same idiom as useCellSelection's columnIdsRef)
  // — mutated during render so the stable callback below always reads the
  // freshest context without needing to be recreated on every change.
  const ctxRef = useRef<SpatialCopyContext>({
    ids: nativeSpatialColumnIds,
    engine,
    dataTypeOf,
    declared: declaredSpatialColumns,
    decoder: null,
  });
  ctxRef.current.ids = nativeSpatialColumnIds;
  ctxRef.current.engine = engine;
  ctxRef.current.dataTypeOf = dataTypeOf;
  ctxRef.current.declared = declaredSpatialColumns;

  const needsDecoder = nativeSpatialColumnIds.size > 0 || declaredSpatialColumns.size > 0;
  useEffect(() => {
    if (!needsDecoder) return;
    let cancelled = false;
    loadSpatialDecoder().then((mod) => {
      if (!cancelled) ctxRef.current.decoder = mod;
    });
    return () => {
      cancelled = true;
    };
  }, [needsDecoder]);

  return useCallback((raw: unknown, columnId: string): unknown => {
    const ctx = ctxRef.current;
    if (!ctx.decoder) return raw;
    if (ctx.engine && ctx.ids.has(columnId)) {
      return ctx.decoder.transformSpatialForExport(raw, ctx.engine, ctx.dataTypeOf(columnId));
    }
    const declaration = ctx.declared.get(columnId);
    if (declaration) {
      return ctx.decoder.transformDeclaredSpatialForExport(raw, declaration);
    }
    return raw;
  }, []);
}
